import { forgotPasswordSchema } from "@rmnlng/shared";
import { useForm } from "@tanstack/react-form";
import { Link, Navigate } from "react-router-dom";
import { FieldError } from "@/components/FieldError";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForgotPassword, useMe } from "@/lib/auth";
import { useErrorMessage } from "@/lib/error-message";
import { useT } from "@/lib/i18n";

/**
 * Step 1 of the "I forgot my password" flow.
 *
 * Single email input → POST /auth/password/forgot → renders the
 * "check your email" confirmation **inline**, replacing the form. We
 * deliberately do *not* route to a separate `/reset-sent` page:
 *   - the URL on the confirmation step is meaningless without the
 *     submission state (refreshing it would just show an empty form),
 *   - keeping it inline lets "Resend" stay a one-click toggle back to
 *     the form pre-filled with the same email.
 *
 * **Server always returns 200**, regardless of whether the email
 * matches a real user — anti-enumeration. The UI shows the same
 * "check your email" copy either way; the email itself (or the
 * absence of one) is the real signal.
 */
export function ForgotPasswordPage() {
  const { data: user } = useMe();
  const forgot = useForgotPassword();
  const t = useT();
  const errorMessage = useErrorMessage();

  const form = useForm({
    defaultValues: { email: "" },
    validators: { onChange: forgotPasswordSchema },
    onSubmit: async ({ value }) => {
      await forgot.mutateAsync(value);
    },
  });

  // If the user is somehow already signed in, send them home — same
  // pattern as Login/SignUp. The forgot flow is only meaningful pre-auth.
  if (user) return <Navigate to="/" replace />;

  // After a successful submit, swap the form for the "check your inbox"
  // confirmation. `forgot.data` flips to truthy on 2xx so we use that
  // as the discriminator rather than juggling a separate `submitted`
  // local-state — fewer moving parts, mutation is the source of truth.
  if (forgot.isSuccess) {
    return <SentConfirmation email={form.state.values.email} onResend={() => forgot.reset()} />;
  }

  return (
    <div className="flex min-h-dvh w-full items-stretch justify-center bg-bg">
      <div className="flex w-full max-w-[440px] flex-col px-7 pt-12 pb-8">
        <div className="mb-12">
          <Logo className="size-12" />
        </div>

        <h1 className="mb-2.5 text-[32px] font-bold leading-[1.1] tracking-[-0.03em]">{t("forgot.title")}</h1>
        <p className="mb-9 text-[15px] leading-[1.5] text-text-3">{t("forgot.subtitle")}</p>

        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          noValidate
        >
          <form.Field name="email">
            {(field) => (
              <div>
                <label htmlFor={field.name} className="mb-1.5 block text-[12px] font-medium text-text-2">
                  {t("forgot.email")}
                </label>
                <Input
                  id={field.name}
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldError errors={field.state.meta.errors} touched={field.state.meta.isTouched} />
              </div>
            )}
          </form.Field>

          {forgot.error && (
            <p className="text-[13px] text-[color:var(--again)]" role="alert">
              {errorMessage(forgot.error)}
            </p>
          )}

          <form.Subscribe
            selector={(s) => ({
              canSubmit: s.canSubmit,
              isSubmitting: s.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <Button type="submit" size="block" className="mt-3" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? t("forgot.submitting") : t("forgot.submit")}
              </Button>
            )}
          </form.Subscribe>
        </form>

        <div className="mt-auto pt-6 text-center text-[13px] text-text-3">
          <Link to="/login" className="font-medium text-[color:var(--accent)]">
            {t("forgot.back_to_login")}
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Inline "check your email" panel. Same shell as the form (logo,
 * max-width, link to login at the bottom) so the visual continuity
 * is preserved — no jarring layout shift on submit.
 */
function SentConfirmation({ email, onResend }: { email: string; onResend: () => void }) {
  const t = useT();
  return (
    <div className="flex min-h-dvh w-full items-stretch justify-center bg-bg">
      <div className="flex w-full max-w-[440px] flex-col px-7 pt-12 pb-8">
        <div className="mb-12">
          <Logo className="size-12" />
        </div>

        <h1 className="mb-2.5 text-[32px] font-bold leading-[1.1] tracking-[-0.03em]">{t("forgot.sent_title")}</h1>
        <p className="mb-9 text-[15px] leading-[1.5] text-text-3">{t("forgot.sent_body", { email })}</p>

        <button
          type="button"
          onClick={onResend}
          className="self-start text-[13px] font-medium text-[color:var(--accent)] underline-offset-4 hover:underline"
        >
          {t("forgot.sent_resend")}
        </button>

        <div className="mt-auto pt-6 text-center text-[13px] text-text-3">
          <Link to="/login" className="font-medium text-[color:var(--accent)]">
            {t("forgot.back_to_login")}
          </Link>
        </div>
      </div>
    </div>
  );
}
